import { Box, Container, Grid, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from "./LanguageSwitcher";
import { colors, company } from "../ui/tokens";

function FooterHeading({ children }) {
  return (
    <Typography component="h3" sx={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: colors.onDarkMuted, mb: 2 }}>
      {children}
    </Typography>
  );
}

function FooterLink({ to, href, children }) {
  const props = href
    ? { component: "a", href, ...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {}) }
    : { component: Link, to };
  return (
    <Box
      {...props}
      sx={{ display: "block", color: colors.onDark, textDecoration: "none", py: 0.6, fontSize: "0.95rem", opacity: 0.88, "&:hover": { opacity: 1, color: colors.orange } }}
    >
      {children}
    </Box>
  );
}

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation('Common');

  return (
    <Box component="footer" sx={{ backgroundColor: colors.navyDeep, color: colors.onDark, pt: { xs: 8, md: 10 }, pb: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 4 }}>
          <Grid item xs={12} md={4}>
            <Box component="img" src="/assets/oxide/oxide-logo-light.svg" alt={company.legalName} sx={{ height: 52, width: "auto", mb: 2.5 }} />
            <Typography sx={{ color: colors.onDarkMuted, fontSize: "0.95rem", maxWidth: 340 }}>{t('footer.blurb')}</Typography>
          </Grid>

          <Grid item xs={6} md={2}>
            <FooterHeading>{t('footer.company')}</FooterHeading>
            <FooterLink to="/products">{t('nav.products')}</FooterLink>
            <FooterLink to="/ourwork">{t('nav.work')}</FooterLink>
            <FooterLink to="/services">{t('nav.services')}</FooterLink>
            <FooterLink to="/aboutus">{t('nav.about')}</FooterLink>
            <FooterLink to="/contact">{t('nav.contact')}</FooterLink>
          </Grid>

          <Grid item xs={6} md={2}>
            <FooterHeading>{t('footer.products')}</FooterHeading>
            <FooterLink to="/project/oxipos">OXIPOS</FooterLink>
            <FooterLink to="/project/posa-sport">Posa Sport</FooterLink>
            <FooterLink href="https://app.oxipos.oxide-digital.com">OXIPOS Web</FooterLink>
            <FooterLink href="https://www.posasport.com">posasport.com</FooterLink>
          </Grid>

          <Grid item xs={12} md={4}>
            <FooterHeading>{t('footer.contact')}</FooterHeading>
            <Typography sx={{ color: colors.onDark, opacity: 0.88, fontSize: "0.95rem", mb: 1.5, lineHeight: 1.7 }}>
              {company.legalName}<br />
              {company.street}<br />
              {company.district}<br />
              {company.city}, {company.country}
            </Typography>
            <FooterLink href={company.phoneHref}>{company.phone}</FooterLink>
            <FooterLink href={`mailto:${company.email}`}>{company.email}</FooterLink>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 6, md: 8 }, pt: 3, borderTop: `1px solid ${colors.lineDark}` }}>
          <Typography sx={{ color: colors.onDarkMuted, fontSize: "0.82rem", lineHeight: 1.9 }}>
            {t('legal.rccm')} {company.rccm}
            <Box component="span" sx={{ mx: 1.25, opacity: 0.5 }}>·</Box>
            {t('legal.idnat')} {company.idNat}
            <Box component="span" sx={{ mx: 1.25, opacity: 0.5 }}>·</Box>
            {t('legal.tax')} {company.taxNo}
            <Box component="span" sx={{ mx: 1.25, opacity: 0.5 }}>·</Box>
            {t('legal.duns')} {company.duns}
          </Typography>
          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ color: colors.onDarkMuted, fontSize: "0.82rem" }}>
              © {currentYear} {company.legalName}. {t('footer.rights')}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Box component={Link} to="/mobilepolicies" sx={{ color: colors.onDarkMuted, fontSize: "0.82rem", "&:hover": { color: colors.onDark } }}>
                {t('footer.privacy')}
              </Box>
              <LanguageSwitcher dark />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
